import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '../../blocks/icons';
import { Button } from '../Button';
import MuiModal, {
	MuiModalProps,
	OnCloseType,
} from '../../mui/MuiModal/MuiModal';
import MuiTextArea from '../../mui/MuiTextArea/MuiTextArea';
import { BundleInput, BundleStatusEnum, UserType } from '#types';
import { FlattenedUser } from '../../types/user';
import rpcShared from '@rockpapercoin/rpc-shared';
import styles from './CustomizeCustomerNotificationModal.module.scss';
import { Box, Typography } from '@mui/material';
import { Spinner } from '../Spinner';
import { TextareaSizesLikeDiv } from './TextareaSizesLikeDiv';
import { Tooltip } from '../Tooltip';
import classNames from 'classnames';

export enum MessagingType {
	ProposalResent = 'ProposalResent',
	ProposalSent = 'ProposalSent',
	ContractResent = 'ContractResent',
	ContractSent = 'ContractSent',
	InvoiceResent = 'InvoiceResent',
	InvoiceSent = 'InvoiceSent',
	Bundle = 'Bundle',
}

type NonInvoiceNonBundleInvoiceEmailMethods =
	| typeof rpcShared.email.constructProposalResentEmailToCustomer
	| typeof rpcShared.email.constructProposalSentEmailToCustomer
	| typeof rpcShared.email.constructContractSentEmailToCustomer
	| typeof rpcShared.email.constructContractResentEmailToCustomer;

const nonInvoiceNonBundleMessagingTypeToMethodDictionary: Record<
| MessagingType.ContractResent
| MessagingType.ContractSent
| MessagingType.ProposalResent
| MessagingType.ProposalSent,
NonInvoiceNonBundleInvoiceEmailMethods
> = {
	[ MessagingType.ProposalResent ]:
		rpcShared.email.constructProposalResentEmailToCustomer,
	[ MessagingType.ProposalSent ]:
		rpcShared.email.constructProposalSentEmailToCustomer,
	[ MessagingType.ContractResent ]:
		rpcShared.email.constructContractResentEmailToCustomer,
	[ MessagingType.ContractSent ]:
		rpcShared.email.constructContractSentEmailToCustomer,
};

type Email = {
	intro: string | undefined;
	links: string;
	outro: string | undefined;
};

export type CustomizedEmail = Pick<Email, 'intro' | 'outro'>;

export type CustomizeCustomerNotificationModalProps = (
	| {
		type: MessagingType.Bundle;
		bundle: BundleInput;
	  }
	| {
		type: MessagingType.InvoiceResent | MessagingType.InvoiceSent;
		unpaidBalance: string;
	  }
	| {
		type: Exclude<MessagingType, 'Bundle' | 'InvoiceResent' | 'InvoiceSent'>;
	  }
) & {
	firstName: string;
	onSend: ( customizedEmail?: CustomizedEmail ) => Promise<boolean>;
	contractCustomers?: number;
} & Pick<MuiModalProps['muiModalProps'], 'open' | 'onClose'>;

export const CustomizeCustomerNotificationModal: React.FC<
CustomizeCustomerNotificationModalProps
> = ( { open, onClose, onSend, firstName, contractCustomers, ...rest } ) => {
	const [ loading, setLoading ] = useState( false );
	const [ email, setEmail ] = useState<Email | undefined>();
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;

	const contractHasMultipleSigners =
		typeof contractCustomers === 'number' && contractCustomers > 1;

	const restType = rest.type;
	const restBundle = 'bundle' in rest ? rest.bundle : undefined;
	const restUnpaidBalance =
		'unpaidBalance' in rest ? rest.unpaidBalance : undefined;

	const resetEmailContents = useCallback( () => {
		if ( !loading ) {
			const possiblyAdjustSalutationForMultipleCustomers = ( copy: string ) =>
				contractHasMultipleSigners
					? copy.replace( `Hi ${ firstName },`, 'Hi,' )
					: copy;
			const documentTypeInBundleStatuses = [ BundleStatusEnum.Awaiting, BundleStatusEnum.Sent, ];
			if ( orgUser ) {
				const contact = {
					firstName,
					vendor: orgUser.organization,
				};
				if ( restType === MessagingType.Bundle && restBundle ) {
					const email = rpcShared.email.constructBundleEmailToCustomer(
						contact,
						{
							...( documentTypeInBundleStatuses.includes(
								restBundle.proposalBundleStatus
							)
								? { proposalLink: false }
								: {} ),
							...( documentTypeInBundleStatuses.includes(
								restBundle.contractBundleStatus
							)
								? { contractLink: false }
								: {} ),
							...( documentTypeInBundleStatuses.includes(
								restBundle.invoiceBundleStatus
							)
								? { invoiceLink: false }
								: {} ),
						}
					);
					setEmail( {
						intro: possiblyAdjustSalutationForMultipleCustomers(
							email.salutation
						),
						links: email.body.links,
						outro: `${ email.body.outro }\n${ email.valediction }\n${ email.sender }`,
					} );
				} else if (
					restType === MessagingType.InvoiceResent ||
					restType === MessagingType.InvoiceSent
				) {
					const email =
						restType === MessagingType.InvoiceResent
							? rpcShared.email.constructInvoiceResentEmailToCustomer(
								contact,
								restUnpaidBalance ?? ''
							  )
							: rpcShared.email.constructInvoiceSentEmailToCustomer(
								contact,
								restUnpaidBalance ?? ''
							  );

					setEmail( {
						intro: `${ possiblyAdjustSalutationForMultipleCustomers(
							email.salutation
						) }\n\n${ email.body.intro }`,
						links: email.body.links,
						outro: `${ email.valediction }\n${ email.sender }`,
					} );
				} else if ( restType !== MessagingType.Bundle ) {
					const email =
						nonInvoiceNonBundleMessagingTypeToMethodDictionary[ restType ](
							contact
						);
					setEmail( {
						intro: `${ possiblyAdjustSalutationForMultipleCustomers(
							email.salutation
						) }\n\n${ email.body.intro }`,
						links: email.body.links,
						outro: `${ email.valediction }\n${ email.sender }`,
					} );
				}
			}
		}
	}, [
		loading,
		orgUser,
		contractHasMultipleSigners,
		firstName,
		restType,
		restBundle,
		restUnpaidBalance,
	] );

	useEffect( () => {
		if ( !open ) {
			/* There's a slight delay in calling onClose and actually closing the modal,
			so to prevent showing the reset contents briefly before closing, lets wait
			for animations and other MUI stuff */
			setTimeout( resetEmailContents, 500 );
		} else if ( typeof email === 'undefined' ) {
			/* If it's open and we're drawing content for the first time, then do that.
			But don't change content when the modal is open and there is already
			content in there */
			resetEmailContents();
		} // not including email below because it creates an endless rerender loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ resetEmailContents, open ] );

	const handleClose: OnCloseType = useCallback(
		( e, reason ) => {
			onClose?.( e, reason );
		},
		[ onClose ]
	);

	const handleSend = useCallback(
		async ( e: React.MouseEvent ) => {
			setLoading( true );
			const customizedEmail = email
				? { intro: email.intro, outro: email.outro }
				: undefined;
			const success = await onSend( customizedEmail );
			if ( success ) {
				handleClose( e, 'fromCode' );
			}
			setLoading( false );
		},
		[
			email,
			onSend,
			handleClose
		]
	);

	const handleIntroChange: React.ChangeEventHandler<HTMLTextAreaElement> =
		useCallback(
			( e ) =>
				setEmail( ( prevState ) =>
					prevState ? { ...prevState, intro: e.target.value } : prevState
				),
			[]
		);

	const handleOutroChange: React.ChangeEventHandler<HTMLTextAreaElement> =
		useCallback(
			( e ) =>
				setEmail( ( prevState ) =>
					prevState ? { ...prevState, outro: e.target.value } : prevState
				),
			[]
		);

	if ( !orgUser ) {
		return null;
	}

	const constructedValue = `${ email?.intro }\n${ email?.links?.replace(
		/<[^>]+>/gm,
		''
	) }\n${ email?.outro ? email.outro : '' }`;

	return (
		<MuiModal
			muiModalProps={ { open, onClose: handleClose } }
			title='Customize email'
			controls={
				<Button
					startIcon={ <Icon type='send' className={ styles.icon } /> }
					loading={ loading }
					onClick={ handleSend }
					className={ styles.sendButton }
					data-cy='customize-email-modal-send-button'
					color='primary'
				>
					{ restType === MessagingType.ContractSent ? 'Prepare' : 'Send' }
				</Button>
			}
		>
			<Box
				className={ classNames(
					styles.container,
					!email ? styles.loading : undefined
				) }
				data-cy='customize-email-modal-content'
			>
				{ !email ? (
					<Spinner />
				) : (
					<>
						<MuiTextArea
							maxLength={ 500 }
							countCharacters
							focused={ true }
							className={ styles.muiTextArea }
							value={ constructedValue }
						/>
						<TextareaSizesLikeDiv
							className={ styles.intro }
							value={ email.intro || '' }
							onChange={ handleIntroChange }
						/>
						<Tooltip
							target={
								<div
									className={ styles.links }
									dangerouslySetInnerHTML={ { __html: email.links } }
								/>
							}
						>
							This portion of the email is not editable
						</Tooltip>
						<TextareaSizesLikeDiv
							className={ styles.outro }
							value={ email.outro || '' }
							onChange={ handleOutroChange }
						/>
					</>
				) }
			</Box>
			{ typeof contractCustomers === 'number' && contractCustomers > 1 ? (
				<Typography className={ styles.caption }>
					*both contract signers will receive the same message
				</Typography>
			) : null }
		</MuiModal>
	);
};
